import { useEffect, useMemo, useState } from 'react';
import { useCrmEmpresaAtiva } from '@/hooks/useCrmEmpresaAtiva';
import { crmBoardsService, crmNegociosService } from '@/services/crmApi';
import { CrmBoard, CrmNegocio } from '@/types/crm';
import { AppLayout } from '@/components/layout/AppLayout';
import { CrmSubNav } from '@/components/crm/CrmSubNav';
import { CrmEmpresaSelector } from '@/components/crm/CrmEmpresaSelector';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Loader2, TrendingUp, Wallet, Target, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { formatMoeda as formatMoedaBase } from '@/lib/formatMoeda';

const MESES_ABREV = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];
const TODOS_VALUE = '__todos__';

// KPIs do relatório mostram valores arredondados (sem centavos) — os demais usos de
// formatMoeda no CRM mantêm 2 casas decimais (padrão do helper compartilhado).
function formatMoeda(valor: number) {
  return formatMoedaBase(valor, { maximumFractionDigits: 0 });
}

function ultimosMeses(qtd: number): { chave: string; label: string }[] {
  const meses: { chave: string; label: string }[] = [];
  const hoje = new Date();
  for (let i = qtd - 1; i >= 0; i--) {
    const d = new Date(hoje.getFullYear(), hoje.getMonth() - i, 1);
    const chave = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    meses.push({ chave, label: `${MESES_ABREV[d.getMonth()]}/${String(d.getFullYear()).slice(2)}` });
  }
  return meses;
}

export default function CrmRelatorios() {
  const { isSuperAdmin, empresas, selectedEmpresaId, setSelectedEmpresaId, empresaId } = useCrmEmpresaAtiva();
  const [boards, setBoards] = useState<CrmBoard[]>([]);
  const [boardId, setBoardId] = useState('');
  const [negocios, setNegocios] = useState<CrmNegocio[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const board = boards.find((b) => b.id === boardId) || null;

  useEffect(() => {
    if (isSuperAdmin && !empresaId) {
      setBoards([]);
      setBoardId('');
      setIsLoading(false);
      return;
    }
    (async () => {
      try {
        setIsLoading(true);
        const result = await crmBoardsService.getAll(isSuperAdmin ? empresaId : undefined);
        if (result.success) {
          setBoards(result.data);
          const padrao = result.data.find((b) => b.is_default) || result.data[0];
          setBoardId(padrao ? padrao.id : '');
        }
      } finally {
        setIsLoading(false);
      }
    })();
  }, [empresaId, isSuperAdmin]);

  const isTodos = boardId === TODOS_VALUE;

  useEffect(() => {
    if (!boardId) {
      setNegocios([]);
      return;
    }
    const promise = isTodos
      ? crmNegociosService.getAll(undefined, isSuperAdmin ? empresaId : undefined)
      : crmNegociosService.getAll(boardId);
    promise.then((result) => {
      if (result.success) setNegocios(result.data);
    });
  }, [boardId, isTodos, isSuperAdmin, empresaId]);

  // Sempre construído a partir de TODOS os boards (não só do selecionado) — no modo "Todos" os
  // negócios vêm de vários boards, e no modo de um board só é equivalente a usar só as dele.
  const colunaPorId = useMemo(() => {
    const map = new Map<string, NonNullable<CrmBoard['colunas']>[number]>();
    boards.forEach((b) => (b.colunas || []).forEach((c) => map.set(c.id, c)));
    return map;
  }, [boards]);

  const metrica = useMemo(() => {
    const abertos = negocios.filter((n) => !n.data_fechamento);
    const fechados = negocios.filter((n) => n.data_fechamento);
    const ganhos = fechados.filter((n) => colunaPorId.get(n.column_id)?.is_ganho);
    const perdidos = fechados.filter((n) => colunaPorId.get(n.column_id)?.is_perdido);

    const valorAberto = abertos.reduce((acc, n) => acc + (Number(n.valor) || 0), 0);
    const valorGanho = ganhos.reduce((acc, n) => acc + (Number(n.valor) || 0), 0);
    const ticketMedio = ganhos.length > 0 ? valorGanho / ganhos.length : 0;
    const totalFechados = ganhos.length + perdidos.length;
    const taxaConversao = totalFechados > 0 ? (ganhos.length / totalFechados) * 100 : 0;

    return { abertos, ganhos, perdidos, valorAberto, valorGanho, ticketMedio, taxaConversao };
  }, [negocios, colunaPorId]);

  const dadosPorEstagio = useMemo(() => {
    const colunas = (board?.colunas || []).slice().sort((a, b) => a.posicao - b.posicao);
    return colunas.map((c) => {
      const doEstagio = negocios.filter((n) => n.column_id === c.id);
      return {
        nome: c.nome,
        cor: c.cor,
        quantidade: doEstagio.length,
        valor: doEstagio.reduce((acc, n) => acc + (Number(n.valor) || 0), 0),
      };
    });
  }, [board, negocios]);

  // Só usado no modo "Todos" — "por estágio" não agrega direito entre boards (nomes de coluna
  // variam por template: "Lead"/"Proposta" do Funil de Vendas x "Novo"/"Resolvido" do Suporte),
  // então a visão agregada quebra por Quadro em vez de por Coluna.
  const dadosPorQuadro = useMemo(() => {
    return boards.map((b) => {
      const doQuadro = negocios.filter((n) => n.board_id === b.id);
      const abertos = doQuadro.filter((n) => !n.data_fechamento);
      return {
        nome: b.nome,
        quantidade: doQuadro.length,
        valor: abertos.reduce((acc, n) => acc + (Number(n.valor) || 0), 0),
      };
    });
  }, [boards, negocios]);

  const dadosPorMes = useMemo(() => {
    const meses = ultimosMeses(6);
    return meses.map(({ chave, label }) => {
      const doMes = negocios.filter((n) => n.data_fechamento?.slice(0, 7) === chave);
      return {
        mes: label,
        ganhos: doMes.filter((n) => colunaPorId.get(n.column_id)?.is_ganho).length,
        perdidos: doMes.filter((n) => colunaPorId.get(n.column_id)?.is_perdido).length,
      };
    });
  }, [negocios, colunaPorId]);

  if (isLoading) {
    return (
      <AppLayout>
        <div className="flex h-64 items-center justify-center">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="animate-fade-in space-y-6">
        <CrmSubNav
          active="relatorios"
          right={
            isSuperAdmin ? (
              <CrmEmpresaSelector empresas={empresas} value={selectedEmpresaId} onChange={setSelectedEmpresaId} />
            ) : undefined
          }
        />
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Relatórios</h1>
            <p className="text-muted-foreground">Funil de vendas e conversão</p>
          </div>
          {boards.length > 0 && (
            <Select value={boardId} onValueChange={setBoardId}>
              <SelectTrigger className="w-[240px]">
                <SelectValue placeholder="Selecione um quadro" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TODOS_VALUE}>Todos os quadros</SelectItem>
                {boards.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.nome}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {isSuperAdmin && !empresaId ? (
          <p className="text-muted-foreground">Selecione uma empresa acima para ver os relatórios.</p>
        ) : boards.length === 0 ? (
          <p className="text-muted-foreground">Nenhum quadro cadastrado ainda.</p>
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <Wallet className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Em aberto</p>
                    <p className="text-lg font-semibold">{metrica.abertos.length}</p>
                    <p className="text-xs text-muted-foreground">{formatMoeda(metrica.valorAberto)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <Target className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Taxa de conversão</p>
                    <p className="text-lg font-semibold">{metrica.taxaConversao.toFixed(0)}%</p>
                    <p className="text-xs text-muted-foreground">
                      {metrica.ganhos.length} ganhos / {metrica.perdidos.length} perdidos
                    </p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <TrendingUp className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Ticket médio (ganhos)</p>
                    <p className="text-lg font-semibold">{formatMoeda(metrica.ticketMedio)}</p>
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="flex items-center gap-3 p-4">
                  <DollarSign className="h-8 w-8 text-muted-foreground" />
                  <div>
                    <p className="text-xs text-muted-foreground">Valor ganho</p>
                    <p className="text-lg font-semibold">{formatMoeda(metrica.valorGanho)}</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">{isTodos ? 'Negócios por quadro' : 'Negócios por estágio'}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={isTodos ? dadosPorQuadro : dadosPorEstagio} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis type="number" allowDecimals={false} className="text-xs" />
                      <YAxis dataKey="nome" type="category" width={100} className="text-xs" />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                        formatter={(value: number, name) => [value, name === 'quantidade' ? 'Negócios' : 'Valor']}
                      />
                      <Bar dataKey="quantidade" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-base">Ganho vs. Perdido (últimos 6 meses)</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-[280px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={dadosPorMes}>
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      <XAxis dataKey="mes" className="text-xs" />
                      <YAxis allowDecimals={false} className="text-xs" />
                      <Tooltip
                        contentStyle={{ backgroundColor: 'hsl(var(--background))', border: '1px solid hsl(var(--border))' }}
                      />
                      <Legend />
                      <Bar dataKey="ganhos" name="Ganhos" fill="#22c55e" radius={[4, 4, 0, 0]} />
                      <Bar dataKey="perdidos" name="Perdidos" fill="#ef4444" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </>
        )}
      </div>
    </AppLayout>
  );
}
